import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { siteWide } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import SvgAccount from '@img/icons/account.svg';
import SvgChevronDown from '@img/icons/angle-down.svg';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import Image from '@stories/Components/Misc/Image/Image';
import { rgba } from 'polished';
import styled from 'styled-components';
const RelativeWrapper = styled.div `
  position: relative;
  height: calc(var(--headerHeight) + var(--bannerHeight, 0px));

  > ${ButtonStyles.Container} {
    position: absolute;
    top: 0;
    left: 0;
    transform: translateY(-105%);

    &:focus-visible {
      transform: translateY(0);
      z-index: ${Layers.HEADER + 1};
    }
  }
`;
const Container = styled.header `
  position: fixed;
  top: 0;
  left: 0;
  display: block;
  background: ${brand.white};
  width: 100%;
  z-index: ${Layers.HEADER};
`;
const TopWrapper = styled.div ``;
const TopInner = styled.div `
  ${siteWide}

  display: flex;
  align-items: center;
  column-gap: var(--gutterWidth);
  height: var(--headerTopHeight);
  justify-content: space-between;
`;
const LogoLink = styled.a `
  display: block;
`;
const Logo = styled(Image) `
  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const LogoMobile = styled(Image) `
  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const SearchWrapper = styled.div `
  display: block;
  flex: 1 1 auto;
  max-width: 576px;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const CtaWrapper = styled.div `
  display: flex;
  column-gap: var(--gutterWidth);
  position: relative;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const NavigationWrapper = styled.div `
  background: ${brand.primary.blueGrey};
`;
const NavigationInner = styled.div `
  ${siteWide}

  display: flex;
  height: var(--headerNavHeight);

  @media ${until(Device.TabletLarge)} {
    justify-content: center;
  }
`;
const NavigationCta = styled.a `
  display: flex;
  align-items: center;
  color: currentColor;
  ${fonts.apparat.medium}
  font-size: ${fluid(14, 18)};
  height: 100%;
  line-height: 1em;
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;

  @media ${from(Device.TabletLarge)} {
    margin-left: auto;
  }

  span {
    padding-top: 3px;
  }

  svg {
    margin-right: ${fluid(6, 8)};
    width: ${fluid(18, 24)};
  }

  &:focus,
  &:hover {
    text-decoration: underline;
  }
`;
const MobileCtaWrapper = styled.div `
  display: flex;
  align-items: center;
  column-gap: 8px;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const MobileCta = styled.button `
  ${ButtonReset}

  display: flex;
  height: 42px;
  place-content: center;
  place-items: center;
  width: 50px;
`;
const MobileSearchCta = styled(MobileCta) `
  svg[data-active] {
    width: 15px;
  }

  svg[data-inactive] {
    width: 37px;
  }
`;
const MobileNavCta = styled(MobileCta) `
  svg[data-active] {
    width: 15px;
  }

  svg[data-inactive] {
    width: 31px;
  }
`;
const WelcomeMessage = styled.span `
  ${fonts.apparat.semiBold};
  position: absolute;
  top: -27px;
  right: 0;
  color: ${rgba(brand.base.blue, 0.65)};
  font-size: 14px;
  line-height: 18px;
`;
const MemberDropdown = styled.div `
  position: relative;
`;
const DropdownBtn = styled.button `
  --borderWidth: 2px;

  --bgSize: 0% 100%;
  --colorFg: ${brand.primary.blue};
  --colorBg: transparent;
  --colorBorder: ${brand.primary.blue};

  --bgSizeActive: 100% 100%;
  --colorFgActive: ${brand.white};
  --colorBgActive: ${brand.primary.blue};
  --colorBorderActive: ${brand.primary.blue};

  --colorFgComplete: ${brand.white};
  --colorBgComplete: ${brand.base.blue};

  display: inline-flex;
  align-items: center;
  border: var(--borderWidth, 0) solid var(--colorBorder, var(--colorBg));
  border-radius: ${fluid(24, 50)};
  background: var(--colorBg);
  background-image: linear-gradient(to right, var(--colorBgActive), var(--colorBgActive));
  background-repeat: no-repeat;
  background-size: var(--bgSize);
  color: var(--colorFg);
  height: ${fluid(40, 50)};
  padding: 0px ${fluid(20, 32)};
  text-decoration: none;
  ${transition('background-size, border-color, opacity')};
  user-select: none;
  width: auto;
  position: relative;
  z-index: 1;

  span& {
    button:hover &,
    a:hover &,
    button:focus &,
    a:focus & {
      border-color: var(--colorBorderActive, var(--colorBgActive));
      color: var(--colorFgActive);
      background-size: var(--bgSizeActive);
    }
  }

  &:not(span) {
    cursor: pointer;

    &:hover,
    &:focus {
      border-color: var(--colorBorderActive, var(--colorBgActive));
      color: var(--colorFgActive);
      background-size: var(--bgSizeActive);
    }
  }

  /* Increase specificity to override :not(span) style */
  &:disabled:disabled:disabled {
    background-size: 100% 100%;
    opacity: 0.5;
    pointer-events: none;
  }

  @media print {
    display: none !important;
  }
`;
const Text = styled.span `
  ${transition('color')};
  display: inline-block;
  padding: 0 10px;
  color: currentColor;
  ${fonts.apparat.semiBold};
  font-size: 18px;
  line-height: 1em;
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;

  [data-icon-only='true'] & {
    ${srOnly};
  }
`;
const AccountSvg = styled(SvgAccount) `
  height: 20px;
`;
const ChevronDownSvg = styled(SvgChevronDown) `
  width: 12px;
`;
const Dropdown = styled.ul `
  position: absolute;
  z-index: 0;
  top: calc(100% - 20px);
  left: 0;
  width: 100%;
  background-color: ${brand.white};
  border: 1px solid ${brand.grey.grey80};
  border-top: 0;
  border-radius: 0 0 20px 20px;
  list-style: none;
  padding: 0;
  margin: 0;
  display: none;

  &[data-is-active='true'] {
    display: block;
  }
`;
const MemberLink = styled.a `
  ${fonts.apparat.light};
  display: block;
  padding: 15px 20px;
  border-top: 1px solid ${brand.grey.grey80};
  color: ${brand.primary.blue};
  text-decoration: none;
  font-size: 18px;
  line-height: 22px;

  &:hover {
    text-decoration: underline;
  }

  li:first-child & {
    border-top: none;
    padding-top: 30px;
  }
`;
const HeaderStyles = {
    AccountSvg,
    ChevronDownSvg,
    Container,
    CtaWrapper,
    Dropdown,
    DropdownBtn,
    Logo,
    LogoLink,
    LogoMobile,
    MemberDropdown,
    MemberLink,
    MobileCtaWrapper,
    MobileNavCta,
    MobileSearchCta,
    NavigationCta,
    NavigationInner,
    NavigationWrapper,
    SearchWrapper,
    Text,
    TopInner,
    TopWrapper,
    RelativeWrapper,
    WelcomeMessage,
};
export default HeaderStyles;
